import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Form, Row, Button } from "react-bootstrap";
import FormControl from "../../components/Common/FormControl";
import FormLabel from "../../components/Common/FormLabel";
import { Select } from "antd";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import FormControlTextArea from "../../components/Common/FormControlTextArea";
import Loader from "../../components/Common/Loader";
import ToastNotification from "../../components/Common/ToastNotification";
import { fetchMacroSplitTemplatesThunk } from "../../redux/Slices/clientMealsTabsSlice";
import { fetchPreferencesAllergies } from "../../redux/Slices/preferencesAllergiesSlice";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";
import RecipeDrawerAI from "./RecipeDrawerAI";
import { useNavigate } from "react-router-dom";

import {
  fetchMealPlanRecipes,
  selectAllRecipesFlat,
  selectMealPlanRecipesLoading,
} from "../../redux/Slices/mealPlanRecipesSlice";

import {
  fetchRecipeImages,
  selectRecipeImagesLoading,
  resetRecipeImages,
} from "../../redux/Slices/recipeImageSlice";

import {
  selectAddReceipeLoading,
  selectAddReceipeSuccess,
  selectAddReceipeError,
  resetAddReceipeState,
  addMealPlanReceipe,
} from "../../redux/Slices/addMealPlanReceipeSlice";

import {
  calculateMacronutrients,
  isValidDecimalInput,
  isValidNumberInput,
  normalizeTitle,
} from "../../utils/Utils";
import FormSelect from "../../components/Common/FormSelect";
import {
  CONSTANTS,
  MEAL_CATEGORIES,
  MEAL_PLAN_TYPES,
  mealTimeAI,
} from "../../utils/Constant/Constant";
import CardRecipes from "./CardRecipes";

const { Option } = Select;

export const mealPlanMap = {
  [MEAL_PLAN_TYPES.TWO_MEALS]: [MEAL_CATEGORIES.LUNCH, MEAL_CATEGORIES.DINNER],
  [MEAL_PLAN_TYPES.THREE_MEALS]: [
    MEAL_CATEGORIES.BREAKFAST,
    MEAL_CATEGORIES.LUNCH,
    MEAL_CATEGORIES.DINNER,
  ],
  [MEAL_PLAN_TYPES.THREE_MEALS_ONE_SNACK]: [
    MEAL_CATEGORIES.BREAKFAST,
    MEAL_CATEGORIES.LUNCH,
    MEAL_CATEGORIES.DINNER,
    MEAL_CATEGORIES.SNACK,
  ],
  [MEAL_PLAN_TYPES.THREE_MEALS_TWO_SNACKS]: [
    MEAL_CATEGORIES.BREAKFAST,
    MEAL_CATEGORIES.LUNCH,
    MEAL_CATEGORIES.DINNER,
    MEAL_CATEGORIES.SNACK,
    MEAL_CATEGORIES.SNACK1,
  ],
  [MEAL_PLAN_TYPES.FOUR_MEALS]: [
    MEAL_CATEGORIES.BREAKFAST,
    MEAL_CATEGORIES.LUNCH,
    MEAL_CATEGORIES.LUNCH1,
    MEAL_CATEGORIES.DINNER,
  ],
  [MEAL_PLAN_TYPES.FOUR_MEALS_ONE_SNACK]: [
    MEAL_CATEGORIES.BREAKFAST,
    MEAL_CATEGORIES.LUNCH,
    MEAL_CATEGORIES.DINNER,
    MEAL_CATEGORIES.SNACK,
    MEAL_CATEGORIES.LUNCH1,
  ],
};

const DIETARY_CONFLICTS_BY_NAME = {
  Pescatarian: ["Vegan"],
  Vegan: ["Perscatarian", "Pescatarian", "Halal", "Kosher", "Dairy-Free"],
  Halal: ["Vegan", "Kosher"],
  Kosher: ["Vegan", "Halal"],
  "Dairy-Free": ["Vegan"],
};

const excludeOptions = [
  { key: "chicken", value: "Chicken" },
  { key: "beef", value: "Beef" },
  { key: "pork", value: "Pork" },
  { key: "turkey", value: "Turkey" },
  { key: "fish", value: "Fish" },
  { key: "shellfish", value: "Shellfish" },
  { key: "eggs", value: "Eggs" },
  { key: "milk", value: "Milk" },
  { key: "cheese", value: "Cheese" },
  { key: "yogurt", value: "Yogurt" },
  { key: "soy", value: "Soy" },
  { key: "tofu", value: "Tofu" },
  { key: "tempeh", value: "Tempeh" },
  { key: "lentils", value: "Lentils" },
  { key: "beans", value: "Beans" },
  { key: "peas", value: "Peas" },
  { key: "nuts", value: "Nuts" },
  { key: "seeds", value: "Seeds" },
  { key: "rice", value: "Rice" },
  { key: "oats", value: "Oats" },
  { key: "bread", value: "Bread" },
  { key: "wheat", value: "Wheat" },
  { key: "citrus-fruits", value: "Citrus fruits" },
  { key: "broccoli", value: "Broccoli" },
  { key: "mushrooms", value: "Mushrooms" },
  { key: "onions", value: "Onions" },
  { key: "garlic", value: "Garlic" },
];

const Recipe = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useFetchCoachProfile();
  const { profileData } = useSelector((state) => state.profile);
  const coachId = profileData?.coachId;

  //coming though slice
  const addLoading = useSelector(selectAddReceipeLoading);
  const addSuccess = useSelector(selectAddReceipeSuccess);
  const addError = useSelector(selectAddReceipeError);

  // Extracts numeric part (e.g. "32g (40%)" -> 32)
  function extractNum(value) {
    if (!value) return "";
    const match = String(value).match(/[\d.]+/);
    return match ? match[0] : "";
  }

  // helper: sanitize base64 string
  function sanitizeBase64(b64) {
    if (!b64) return "";
    let clean = String(b64).replace(/[\r\n\s]+/g, "");
    const pad = clean.length % 4;
    if (pad > 0) clean += "=".repeat(4 - pad);
    return clean;
  }

  // main builder
  const buildReceipeDetails = async (recipesSource, selectedSet, imagesMap) => {
    const selectedList =
      selectedSet && selectedSet.size > 0
        ? recipesSource.filter((recipe) => selectedSet.has(recipe.title))
        : [];

    const hasAlphaUnit = (value) =>
      typeof value === "string" && /[a-zA-Z]/.test(value);
    const toStringOrEmpty = (value) =>
      value === 0 || value ? String(value) : "";
    const ensureUnit = (value, unit) =>
      value === 0 || value
        ? hasAlphaUnit(value)
          ? String(value)
          : `${value}${unit}`
        : "";
    const kcalize = (val) => {
      const num = String(val ?? "").match(/([\d.]+)/)?.[1];
      return num ? `${num}kcal` : "";
    };

    return Promise.all(
      selectedList.map(async (recipe) => {
        // Get edited version if available
        const editedRecipe = editedRecipes.get(recipe.title);
        const finalRecipe = editedRecipe || recipe;

        const nutrients =
          finalRecipe?.nutritionPerServing || finalRecipe?.nutrition || {};
        const normalizedKey = normalizeTitle(finalRecipe.title);

        // image lookup
        const matchedImage =
          imagesMap?.[normalizedKey] ?? imagesMap?.[recipe.title] ?? null;

        let recipeImageFields = {
          recipeImageBase64: "",
          recipeImageFileName: "",
          recipeImageMediaType: "",
          recipeImageFileType: "",
          recipeImageSourceType: "",
        };

        if (matchedImage?.imageBase64) {
          recipeImageFields = {
            recipeImageBase64: sanitizeBase64(matchedImage.imageBase64),
            recipeImageFileName: "recipe Picture.png",
            recipeImageMediaType: "image",
            recipeImageFileType: matchedImage.imageFileType || "image/png",
            recipeImageSourceType: "recipe",
          };
        }

        // Use edited ingredients if available
        const ingredientList = editedRecipe?.ingredients
          ? editedRecipe.ingredients.map((item) => ({
              ingredient: item.ingredient,
              quantityMetric: item.quantityinMetric || "",
              unitMetric: item.measureinMetric || "",
              quantityImperial: item.quantityinImperial || "",
              unitImperial: item.measureinImperial || "",
            }))
          : Array.isArray(finalRecipe.ingredients)
          ? finalRecipe.ingredients.map((item) => ({
              ingredient: item.ingredient,
              quantityMetric: item.quantityinMetric || "",
              unitMetric: item.measureinMetric || "",
              quantityImperial: item.quantityinImperial || "",
              unitImperial: item.measureinImperial || "",
            }))
          : [];

        const ingredients =
          editedRecipe?.ingredientLines ||
          (Array.isArray(finalRecipe.ingredients)
            ? finalRecipe.ingredients.map((ing) =>
                typeof ing === "string"
                  ? ing
                  : [ing.quantityinMetric, ing.measureinMetric, ing.ingredient]
                      .filter(Boolean)
                      .join(" ")
              )
            : []);

        // Use edited nutrition values if available
        const finalNutrients = editedRecipe?.nutrientsPerServing
          ? { ...nutrients, ...editedRecipe.nutrientsPerServing }
          : nutrients;

        return {
          title: finalRecipe.title || finalRecipe.name || "",
          recipeTypeId:
            finalRecipe.recipeId ||
            finalRecipe.recipeTypeId ||
            finalRecipe.id ||
            "",
          course:
            finalRecipe.course ||
            finalRecipe.mealType ||
            finalRecipe.category ||
            "",
          preptime:
            finalRecipe.prepTime ||
            finalRecipe.preptime ||
            finalRecipe.prep_time ||
            "",
          // FIX: Use finalRecipe instead of recipe for these fields
          allergies: finalRecipe.allergies || recipe.allergies,
          preferences: finalRecipe.preferences || recipe.preferences,
          exclude: finalRecipe.exclude || recipe.exclude,
          servings: finalRecipe.servings || recipe.servings,
          ingredientsCount: toStringOrEmpty(
            Array.isArray(ingredients)
              ? ingredients.length
              : finalRecipe.ingredientsCount
          ),
          difficulty: finalRecipe.difficulty || "",
          ingredientList,
          ingredients,
          instructions: Array.isArray(finalRecipe.instructions)
            ? finalRecipe.instructions
            : Array.isArray(finalRecipe.steps)
            ? finalRecipe.steps
            : [],

          // Use edited nutrition values
          nutrientsPerserving: {
            calories: kcalize(finalNutrients.calories),
            protein_g: ensureUnit(
              finalNutrients.protein_g || finalNutrients.protein,
              "g"
            ),
            protein_pct:
              extractNum(finalNutrients.protein_pct) ||
              editedRecipe?.macroPercentages?.protein ||
              "0",
            carbs_g: ensureUnit(
              finalNutrients.carbs_g || finalNutrients.carbs,
              "g"
            ),
            carbs_pct:
              extractNum(finalNutrients.carbs_pct) ||
              editedRecipe?.macroPercentages?.carbs ||
              "0",
            fat_g: ensureUnit(finalNutrients.fat_g || finalNutrients.fat, "g"),
            fat_pct:
              extractNum(finalNutrients.fat_pct) ||
              editedRecipe?.macroPercentages?.fat ||
              "0",
            fiber: toStringOrEmpty(finalNutrients.fiber),
            calcium: ensureUnit(finalNutrients.calcium, "mg"),
            iron: ensureUnit(finalNutrients.iron, "mg"),
            vitaminD: toStringOrEmpty(finalNutrients.vitaminD),
            vitaminB12: toStringOrEmpty(finalNutrients.vitaminB12),
            magnesium: ensureUnit(finalNutrients.magnesium, "mg"),
            saturatedFat: toStringOrEmpty(finalNutrients.saturatedFat),
            monounsaturatedFat: toStringOrEmpty(
              finalNutrients.monounsaturatedFat
            ),
            transFat: toStringOrEmpty(finalNutrients.transFat),
            cholesterol: toStringOrEmpty(finalNutrients.cholesterol),
            potassium: ensureUnit(finalNutrients.potassium, "mg"),
            vitaminA: toStringOrEmpty(finalNutrients.vitaminA),
            vitaminC: ensureUnit(
              finalNutrients.vitaminC || finalNutrients.vitamin_c,
              "mg"
            ),
            sodium: ensureUnit(finalNutrients.sodium, "mg"),
            sugar:ensureUnit(finalNutrients.sugar, "g"),
            netcarbs: toStringOrEmpty(finalNutrients.netcarbs),
          },

          // === image fields ===
          ...recipeImageFields,
        };
      })
    );
  };
  const handleRecipeUpdate = (updatedRecipe) => {
    setEditedRecipes((prev) => {
      const newMap = new Map(prev);

      // Find the original recipe to preserve fields like allergies, preferences, etc.
      const originalRecipe = allRecipes.find(
        (r) => r.title === updatedRecipe.name
      );

      // Create updated recipe data structure with preserved fields
      const apiFormattedRecipe = {
        ...originalRecipe,
        ...updatedRecipe,

        // Update ingredients array with edited values
        ingredients:
          updatedRecipe.ingredients ||
          updatedRecipe.ingredientLines?.map((line, index) => {
            const parts = line.split(" ");
            const originalIngredient = originalRecipe?.ingredients?.[index];

            return {
              ingredient: parts.slice(2).join(" "),
              quantityinMetric:
                updatedRecipe.ingredients?.[index]?.quantityinMetric ||
                parts[0] ||
                "",
              measureinMetric:
                updatedRecipe.ingredients?.[index]?.measureinMetric ||
                parts[1] ||
                "",
              quantityinImperial:
                updatedRecipe.ingredients?.[index]?.quantityinImperial ||
                originalIngredient?.quantityinImperial ||
                "",
              measureinImperial:
                updatedRecipe.ingredients?.[index]?.measureinImperial ||
                originalIngredient?.measureinImperial ||
                "",
            };
          }),

        // Store nutrition with manual percentages
        nutritionPerServing: {
          ...originalRecipe?.nutritionPerServing,
          ...updatedRecipe.nutrientsPerServing,
          protein_g:
            updatedRecipe.nutrientsPerServing?.protein ||
            updatedRecipe.nutrientsPerServing?.protein_g,
          carbs_g:
            updatedRecipe.nutrientsPerServing?.carbs ||
            updatedRecipe.nutrientsPerServing?.carbs_g,
          fat_g:
            updatedRecipe.nutrientsPerServing?.fat ||
            updatedRecipe.nutrientsPerServing?.fat_g,
          protein_pct: updatedRecipe.macroPercentages?.protein
            ? `${updatedRecipe.macroPercentages.protein}%`
            : originalRecipe?.nutritionPerServing?.protein_pct,
          carbs_pct: updatedRecipe.macroPercentages?.carbs
            ? `${updatedRecipe.macroPercentages.carbs}%`
            : originalRecipe?.nutritionPerServing?.carbs_pct,
          fat_pct: updatedRecipe.macroPercentages?.fat
            ? `${updatedRecipe.macroPercentages.fat}%`
            : originalRecipe?.nutritionPerServing?.fat_pct,
        },

        // Store relative calories for drawer display
        relativeCalories: {
          carbs: updatedRecipe.macroPercentages?.carbs
            ? Number(updatedRecipe.macroPercentages.carbs) / 100
            : updatedRecipe.relativeCalories?.carbs,
          protein: updatedRecipe.macroPercentages?.protein
            ? Number(updatedRecipe.macroPercentages.protein) / 100
            : updatedRecipe.relativeCalories?.protein,
          fat: updatedRecipe.macroPercentages?.fat
            ? Number(updatedRecipe.macroPercentages.fat) / 100
            : updatedRecipe.relativeCalories?.fat,
        },
      };

      newMap.set(updatedRecipe.name, apiFormattedRecipe);
      return newMap;
    });

    // Close edit mode for this recipe
    setEditingRecipeId(null);
  };

  const handleSaveReceipes = async () => {
    const baseSelection =
      (lockedTitles?.size ? lockedTitles : selectedTitles) || new Set();
    if (baseSelection.size === 0) {
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: "Please select at least one recipe card before saving.",
        heading: trans("Common.failed"),
      });
      setTimeout(() => setToastConfig(null), CONSTANTS.TOAST_DURATION_SHORT);
      return;
    }

    const details = await buildReceipeDetails(
      allRecipes,
      baseSelection,
      imagesByTitle
    );

    if (!details.length) {
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: "Nothing to save. No matching recipe details found.",
        heading: trans("Common.failed"),
      });
      setTimeout(() => setToastConfig(null), CONSTANTS.TOAST_DURATION_SHORT);
      return;
    }

    const payload = {
      coachId: coachId || "",
      recipeDetails: details,
    };

    try {
      await dispatch(addMealPlanReceipe(payload)).unwrap();
    } catch (e) {
      console.error("[AddMealPlanReceipe] Error →", e);
    }
  };

  const handleGenerateImages = async () => {
    // Hard re-entry guard (instant, no re-render needed)
    if (generatingRef.current || isGeneratingImages) return;

    // Stop if nothing is selected
    if (selectedTitles.size === 0) {
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: "Please select at least one recipe card.",
        heading: trans("Common.failed"),
      });
      setTimeout(() => setToastConfig(null), CONSTANTS.TOAST_DURATION_SHORT);
      return;
    }

    try {
      generatingRef.current = true; // lock immediately
      setIsGeneratingImages(true); // UI disable

      dispatch(resetRecipeImages());
      fetchedTitlesRef.current.clear();

      // Only selected recipes
      const source = allRecipes.filter((r) => selectedTitles.has(r.title));

      // Dedup titles
      const titlesNeedingImages = Array.from(
        new Set(source.map((r) => r.title).filter(Boolean))
      );
      if (titlesNeedingImages.length === 0) return;

      const payload = {
        image: titlesNeedingImages.map((title) => {
          // const r = source.find((rec) => rec.title === title);
          return {
            title,
          };
        }),
      };

      await dispatch(fetchRecipeImages(payload)).unwrap();
      setHasGeneratedImages(true);

      // snapshot current selection so filtering won't change on further clicks
      setLockedTitles(new Set(selectedTitles));

      setShowOnlySelected(true);
    } catch (err) {
      console.error("[Images] fetch error →", err);
    } finally {
      setIsGeneratingImages(false);
      generatingRef.current = false; // release lock
    }
  };

  const { macroSplitTemplates, macroSplitTemplatesLoading } = useSelector(
    (state) => state.mealTags
  );
  const { preferences, allergies } = useSelector(
    (state) => state.preferencesAllergies
  );
  const { trans } = useCustomTranslation();

  const { loading, createMealPlanLoading, mealPlanDetailsLoading } =
    useSelector((state) => state.mealTags);

  const [dietaryPreferences, setDietaryPreferences] = useState([]);
  const [allergiesState, setAllergiesState] = useState([]);
  const [selectedMeals, setSelectedMeals] = useState("");
  const [toastConfig, setToastConfig] = useState(null);

  const [mealPercentages, setMealPercentages] = useState({});
  //check total percentage of protein, carb and fat should 100%
  const [isPercentageValid, setIsPercentageValid] = useState(true);
  // Check each meal percentage should be 100%
  const [isEachMealPercentageValid, setIsEachMealPercentageValid] =
    useState(true);
  // Macro Split Templates
  const [selectedMacroTemplate, setSelectedMacroTemplate] = useState(null);

  const [formData, setFormData] = useState({
    bmrTdee: "",
    pal: "",
    mealPlanName: "",
    dailyIntake: "",
    proteinPercentage: "",
    proteinGrams: "",
    carbsPercentage: "",
    carbsGrams: "",
    fatPercentage: "",
    fatGrams: "",
    mealDetails: [],
    cookTime: 5,
    edgeCaseRequests: "",
    noPreference: false,
    exclude: [],
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fetchedTitlesRef = React.useRef(new Set());
  const generatingRef = React.useRef(false);

  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [hasGeneratedImages, setHasGeneratedImages] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRecipe, setDrawerRecipe] = useState(null);
  // Add these states at the top of your component:
  const [editedRecipes, setEditedRecipes] = useState(new Map());
  const [editingRecipeId, setEditingRecipeId] = useState(null); // Track which recipe is being edited

  const allRecipes = useSelector(selectAllRecipesFlat);
  const recipesLoading = useSelector(selectMealPlanRecipesLoading);
  const hasResults = allRecipes.length > 0;
  const showRecipes = isSubmitted && hasResults;
  const imagesByTitle = useSelector(
    (state) => state.recipeImages.byTitle || {}
  );
  const imagesLoading = useSelector(selectRecipeImagesLoading);

  const canShowSave =
    hasGeneratedImages &&
    !imagesLoading &&
    Object.keys(imagesByTitle || {}).length > 0;

  //  selection state (multi-select)
  const [selectedTitles, setSelectedTitles] = useState(new Set());
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  // snapshot of selection after generation
  const [lockedTitles, setLockedTitles] = useState(null);

  // hide unselected when there is a selection
  const visibleRecipes =
    showOnlySelected && lockedTitles?.size > 0
      ? allRecipes.filter((r) => lockedTitles.has(r.title))
      : allRecipes;

  const MEALS_API_MAP = {
    "2Meals": "2 meals",
    "3Meals": "3 meals",
    "3Meals1Snack": "3 meals + 1 snacks",
    "3Meals2Snacks": "3 meals + 2 snacks",
    "4Meals": "4 meals",
    "4Meals1Snack": "4 meals + 1 snacks",
  };

  useEffect(() => {
    dispatch(fetchPreferencesAllergies());
    dispatch(fetchMacroSplitTemplatesThunk());
  }, [dispatch]);

  // Clear images on mount / refresh
  useEffect(() => {
    dispatch(resetRecipeImages());
  }, [dispatch]);

  // Check percentage totals for protien carbs and fats
  useEffect(() => {
    const protein = Number(formData.proteinPercentage) || 0;
    const carbs = Number(formData.carbsPercentage) || 0;
    const fat = Number(formData.fatPercentage) || 0;

    const total = protein + carbs + fat;
    setIsPercentageValid(total === 100);
  }, [
    formData.proteinPercentage,
    formData.carbsPercentage,
    formData.fatPercentage,
  ]);

  // Check if each meal percentage is valid
  useEffect(() => {
    const total = Object.values(mealPercentages).reduce(
      (a, b) => a + Number(b),
      0
    );
    const eachMealValid = total === 100;
    setIsEachMealPercentageValid(eachMealValid);
  }, [mealPercentages]);

  useEffect(() => {
    if (!macroSplitTemplates?.length) return;

    // 1) If nothing is selected yet, default to the first template
    if (!selectedMacroTemplate) {
      const first = macroSplitTemplates[0];
      setSelectedMacroTemplate(first.id);
      setFormData((prev) => ({
        ...prev,
        proteinPercentage: first.protein,
        carbsPercentage: first.carbs,
        fatPercentage: first.fats,
      }));
      return;
    }

    // 2) When a template is selected (except Custom = 11), fill percentages
    const chosen = macroSplitTemplates.find(
      (t) => t.id === selectedMacroTemplate
    );
    if (chosen && chosen.id !== 11) {
      setFormData((prev) => ({
        ...prev,
        proteinPercentage: chosen.protein,
        carbsPercentage: chosen.carbs,
        fatPercentage: chosen.fats,
      }));
    }
  }, [macroSplitTemplates, selectedMacroTemplate]);

  // Add a useEffect to calculate macronutrients when percentages or daily intake change
  useEffect(() => {
    if (formData.dailyIntake) {
      const { protein, carbs, fat } = calculateMacronutrients(
        Number(formData.dailyIntake),
        Number(formData.proteinPercentage),
        Number(formData.carbsPercentage),
        Number(formData.fatPercentage)
      );

      setFormData((prev) => ({
        ...prev,
        proteinGrams: protein.toFixed(2),
        carbsGrams: carbs.toFixed(2),
        fatGrams: fat.toFixed(2),
      }));
    }
  }, [
    formData.dailyIntake,
    formData.proteinPercentage,
    formData.carbsPercentage,
    formData.fatPercentage,
  ]);

  useEffect(() => {
    if (addSuccess) {
      setToastConfig({
        type: CONSTANTS.TOAST_SUCCESS,
        message: addSuccess || "Receipe(s) saved successfully.",
        heading: trans("Common.success"),
      });

      setTimeout(() => {
        setToastConfig(null);
        dispatch(resetAddReceipeState());
        // navigate after success + toast
        navigate("/recipe-success");
      }, CONSTANTS.TOAST_DURATION_SHORT);
    }
  }, [addSuccess, dispatch, navigate, trans]);

  useEffect(() => {
    if (addError) {
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message:
          typeof addError === "string"
            ? addError
            : "Failed to save receipe(s).",
        heading: trans("Common.failed"),
      });
      setTimeout(() => {
        setToastConfig(null);
        dispatch(resetAddReceipeState());
      }, CONSTANTS.TOAST_DURATION_SHORT);
    }
  }, [addError, dispatch, trans]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for different input types
    if (name === CONSTANTS.DAILY_INTAKE) {
      if (!isValidNumberInput(value) || value.length > CONSTANTS.FOUR) return;
    } else if (
      name === "proteinPercentage" ||
      name === "carbsPercentage" ||
      name === "fatPercentage"
    ) {
      if (value !== "" && !isValidNumberInput(value)) return;
    } else if (
      name === "proteinGrams" ||
      name === "carbsGrams" ||
      name === "fatGrams"
    ) {
      if (value !== "" && !isValidDecimalInput(value)) return;
    }

    // Update form data first
    setFormData((prev) => {
      const newFormData = { ...prev, [name]: value };

      // Calculate the new percentages total with the updated value
      const totalPercentage =
        Number(
          name === "proteinPercentage" ? value : prev.proteinPercentage || 0
        ) +
        Number(name === "carbsPercentage" ? value : prev.carbsPercentage || 0) +
        Number(name === "fatPercentage" ? value : prev.fatPercentage || 0);

      if (
        (name === "proteinPercentage" ||
          name === "carbsPercentage" ||
          name === "fatPercentage") &&
        totalPercentage === 100 &&
        isEachMealPercentageValid
      ) {
        // No additional action needed here, so block removed
      }

      return newFormData;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.dailyIntake)
      newErrors.dailyIntake =
        trans("ClientMealPlan.dailyIntake") + " " + trans("Common.isRequired");

    if (!selectedMeals || selectedMeals === "") {
      newErrors.selectedMeals = "Select meal is required";
    }

    // Convert percentages to numbers and check if their total exceeds 100%
    const protein = Number(formData.proteinPercentage) || CONSTANTS.ZERO;
    const carbs = Number(formData.carbsPercentage) || CONSTANTS.ZERO;
    const fat = Number(formData.fatPercentage) || CONSTANTS.ZERO;

    // Check if any value is zero or negative
    if (protein <= CONSTANTS.FIVE) {
      newErrors.proteinPercentage = trans(
        "ClientMealPlan.proteinMustBeGreaterThanFive"
      );
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: trans("ClientMealPlan.proteinMustBeGreaterThanFive"),
        heading: trans("Common.failed"),
      });

      setTimeout(() => {
        setToastConfig(null);
      }, CONSTANTS.TOAST_DURATION_SHORT);
    }
    if (carbs <= CONSTANTS.FIVE) {
      newErrors.carbsPercentage = trans(
        "ClientMealPlan.carbsMustBeGreaterThanFive"
      );
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: trans("ClientMealPlan.carbsMustBeGreaterThanFive"),
        heading: trans("Common.failed"),
      });

      setTimeout(() => {
        setToastConfig(null);
      }, CONSTANTS.TOAST_DURATION_SHORT);
    }
    if (fat <= CONSTANTS.FIVE) {
      newErrors.fatPercentage = trans(
        "ClientMealPlan.fatMustBeGreaterThanFive"
      );
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: trans("ClientMealPlan.fatMustBeGreaterThanFive"),
        heading: trans("Common.failed"),
      });

      setTimeout(() => {
        setToastConfig(null);
      }, CONSTANTS.TOAST_DURATION_SHORT);
    }

    // Check if total percentage exceeds 100
    else if (!isPercentageValid) {
      newErrors.proteinPercentage = trans(
        "ClientMealPlan.proteinPercentageError"
      );
      newErrors.carbsPercentage = trans(
        "ClientMealPlan.proteinPercentageError"
      );
      newErrors.fatPercentage = trans("ClientMealPlan.proteinPercentageError");

      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: trans("ClientMealPlan.proteinPercentageError"),
        heading: trans("Common.failed"),
      });

      setTimeout(() => {
        setToastConfig(null);
      }, CONSTANTS.TOAST_DURATION_SHORT);
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === CONSTANTS.ZERO;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      let cooktimeValue;
      if (formData.noPreference) {
        cooktimeValue = "No Preference";
      } else if (formData.cookTime) {
        cooktimeValue = `${formData.cookTime} minutes`;
      } else {
        cooktimeValue = null;
      }
      // Payload for  Getrecipes API
      const requestPayload = {
        bmr_tdee: Number(formData.bmrTdee) || 0,
        pal: formData.pal,
        allergies: allergiesState
          .map(
            (id) =>
              allergies?.options?.find((o) => o.optionId === id)?.optionText
          )
          .filter(Boolean),
        preferences: dietaryPreferences
          .map(
            (id) =>
              preferences?.options?.find((o) => o.optionId === id)?.optionText
          )
          .filter(Boolean)
          .join(","),
        exclude: formData.exclude,
        daily_intake: Number(formData.dailyIntake) || 0,
        cooktime: cooktimeValue,
        edge_case_request: formData.edgeCaseRequests,
        meals: MEALS_API_MAP[selectedMeals] ?? selectedMeals,
        macro_split: `P:${formData.proteinPercentage} C:${formData.carbsPercentage} F:${formData.fatPercentage}`,
      };

      dispatch(resetRecipeImages());
      //  reset filter/lock before re-generating recipes
      setShowOnlySelected(false);
      setLockedTitles(null);
      setHasGeneratedImages(false);

      dispatch(fetchMealPlanRecipes(requestPayload));

      setIsSubmitted(true);
    }
  };

  useEffect(() => {
    // Only set default percentages if mealPercentages is empty
    if (Object.keys(mealPercentages).length === 0) {
      const selectedPlan = mealPlanMap[selectedMeals] || [];
      const getDefaultPercentage = (meal) => {
        const lowerMeal = meal.toLowerCase();
        if (lowerMeal.includes("breakfast")) return 20;
        if (lowerMeal === "lunch") return 20;
        if (lowerMeal === "lunch1") return 25;
        if (lowerMeal.includes("snack1")) return 10;
        if (lowerMeal.includes("snack2")) return 10;
        if (lowerMeal === "snack") return 10;
        if (lowerMeal === "dinner") return 25;
        return 0;
      };

      const updated = {};
      selectedPlan.forEach((meal) => {
        updated[meal] = getDefaultPercentage(meal);
      });
      setMealPercentages(updated);
    }
  }, [selectedMeals, mealPercentages]);

  const handleMealsChange = async (e) => {
    const newSelectedMeals = e.target.value;
    dispatch(resetRecipeImages());
    setSelectedMeals(newSelectedMeals);
    setSelectedTitles(new Set()); // clear selection when meal plan changes
    setShowOnlySelected(false); // reset: show all cards again
    setLockedTitles(null); // clear locked snapshot
    setFormData((prev) => ({ ...prev, mealDetails: [] }));
    setMealPercentages({});
    setHasGeneratedImages(false);
  };

  const handleMacroTemplateChange = (value) => {
    const template = macroSplitTemplates.find((t) => t.id === value);
    setSelectedMacroTemplate(value);

    if (template && template.id !== 11) {
      // Not "Customize"
      setFormData((prev) => ({
        ...prev,
        proteinPercentage: template.protein,
        carbsPercentage: template.carbs,
        fatPercentage: template.fats,
      }));
    } else {
      // Reset to empty for custom values
      setFormData((prev) => ({
        ...prev,
        proteinPercentage: "",
        carbsPercentage: "",
        fatPercentage: "",
      }));
    }
  };

  const handleChangeAllergies = (selectedIds) => {
    setAllergiesState(selectedIds);
  };

  const handleCheckboxChange = (e) => {
    const { checked } = e.target;
    setFormData((prev) => ({ ...prev, noPreference: checked }));

    // Clear cook time if "No Preference" is checked
    if (checked) {
      setFormData((prev) => ({ ...prev, cookTime: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getDisabledPreferences = (selected, allOptions) => {
    const disabledSet = new Set();

    selected.forEach((prefId) => {
      const prefText = allOptions.find(
        (o) => o.optionId === prefId
      )?.optionText;
      if (prefText && DIETARY_CONFLICTS_BY_NAME[prefText]) {
        DIETARY_CONFLICTS_BY_NAME[prefText].forEach((conflict) =>
          disabledSet.add(conflict)
        );
      }
    });

    // Do not disable already-selected ones
    selected.forEach((prefId) => {
      const prefText = allOptions.find(
        (o) => o.optionId === prefId
      )?.optionText;
      disabledSet.delete(prefText);
    });

    return disabledSet;
  };

  const pctToFraction = (v) => {
    if (v === 0 || v) {
      const num = parseFloat(String(v).replace("%", ""));
      return Number.isFinite(num) ? num / 100 : undefined;
    }
    return undefined;
  };

  // Update the makeDrawerRecipe function to use edited data:
  const makeDrawerRecipe = (r) => {
    const editedRecipe = editedRecipes.get(r.title);
    const finalRecipe = editedRecipe || r;

    const n =
      finalRecipe?.nutritionPerServing ||
      finalRecipe?.nutrientsPerServing ||
      {};

    // Use updated images map
    const imgData = imagesByTitle[normalizeTitle(r.title)];

    return {
      name: finalRecipe.title || finalRecipe.name || "",
      mainImage: imgData?.imageBase64
        ? `data:${imgData.imageFileType || "image/png"};base64,${
            imgData.imageBase64
          }`
        : "",
      prepTime: finalRecipe.prepTime || finalRecipe.preptime || "",
      numberOfServings: r.servings,

      // Include original ingredients array for editing
      ingredients:
        finalRecipe.ingredients ||
        (Array.isArray(r.ingredients) ? r.ingredients : []),

      ingredientLines:
        editedRecipe?.ingredientLines ||
        (Array.isArray(finalRecipe.ingredients)
          ? finalRecipe.ingredients.map((i) =>
              [i.quantityinMetric, i.measureinMetric, i.ingredient]
                .filter(Boolean)
                .join(" ")
            )
          : []),

      instructions: (() => {
        if (Array.isArray(finalRecipe.instructions))
          return finalRecipe.instructions;
        if (Array.isArray(finalRecipe.steps)) return finalRecipe.steps;
        return [];
      })(),

      relativeCalories: finalRecipe.relativeCalories || {
        carbs: pctToFraction(n.carbs_pct),
        protein: pctToFraction(n.protein_pct),
        fat: pctToFraction(n.fat_pct),
      },

      nutrientsPerServing: {
        carbs: n.carbs_g || n.carbs,
        protein: n.protein_g || n.protein,
        fat: n.fat_g || n.fat,
        calories: extractNum(n.calories),
        calcium: extractNum(n.calcium),
        iron: extractNum(n.iron),
        magnesium: extractNum(n.magnesium),
        potassium: extractNum(n.potassium),
        sodium: extractNum(n.sodium),
        sugar: extractNum(n.sugar),
        vitaminC: extractNum(n.vitaminC || n.vitamin_c),
        fiber: extractNum(n.fiber),
      },
    };
  };

  const handleInfoOpen = (idOrTitle) => {
    const r =
      allRecipes.find((x) => x?.recipeId === idOrTitle) ||
      allRecipes.find((x) => x?.title === idOrTitle);
    if (!r) return;

    setEditingRecipeId(r.title);
    setDrawerRecipe(makeDrawerRecipe(r));
    setDrawerOpen(true);
  };

  return (
    <div className="clientDetailsInfoTabContainer">
      {loading || createMealPlanLoading || mealPlanDetailsLoading ? (
        <Loader />
      ) : (
        <>
          <Row className="align-items-center">
            <Col>
              <h6>{trans("ClientMealPlan.nutritionInformation")}</h6>
            </Col>
          </Row>
          <Form onSubmit={handleSubmit}>
            {/* BMR / PAL / Preferences / Allergies / Exclude / Daily Intake */}
            <Row className="g-3">
              {/* BMR/TDEE */}
              <Col xs={12} md={3}>
                <FormLabel label="BMR/TDEE" />
                <FormControl
                  type="text"
                  name="bmrTdee"
                  placeholder="Enter BMR/TDEE"
                  value={formData.bmrTdee}
                  onChange={handleChange}
                  className="rounded-3"
                  suffix="Cal"
                />
              </Col>

              {/* PAL */}
              <Col xs={12} md={3}>
                <FormLabel label="PAL" />
                <FormControl
                  type="text"
                  name="pal"
                  placeholder="Enter PAL"
                  value={formData.pal}
                  onChange={handleChange}
                  className="rounded-3"
                />
              </Col>

              {/* Preferences*/}
              <Col xs={12} md={3}>
                <FormLabel label={trans("ClientMealPlan.preferences")} />
                <Select
                  mode="multiple"
                  placeholder={
                    trans("Common.select") +
                    " " +
                    trans("ClientMealPlan.preferences")
                  }
                  value={dietaryPreferences}
                  onChange={setDietaryPreferences}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  className="w-100"
                >
                  {preferences?.options?.map((option) => {
                    const disabledSet = getDisabledPreferences(
                      dietaryPreferences,
                      preferences?.options || []
                    );
                    return (
                      <Select.Option
                        key={option.optionId}
                        value={option.optionId}
                        disabled={disabledSet.has(option.optionText)}
                      >
                        {option.optionText}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Col>

              {/* Allergies */}
              <Col xs={12} md={3}>
                <FormLabel label={trans("ClientMealPlan.allergies")} />
                <Select
                  mode="multiple"
                  placeholder={
                    trans("Common.select") +
                    " " +
                    trans("ClientMealPlan.allergies")
                  }
                  value={allergiesState}
                  onChange={handleChangeAllergies}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  className="w-100"
                >
                  {allergies?.options?.map((option) => (
                    <Select.Option
                      key={option.optionId}
                      value={option.optionId}
                    >
                      {option.optionText}
                    </Select.Option>
                  ))}
                </Select>
              </Col>

              {/* Exclude */}
              <Col xs={12} md={3}>
                <Form.Group controlId="exclude">
                  <FormLabel label={trans("ClientMealPlan.Exclude")} />
                  <Select
                    mode="multiple"
                    name="exclude"
                    value={formData.exclude}
                    onChange={(value) => handleSelectChange("exclude", value)}
                    placeholder="Select Exclude"
                    style={{ width: "100%" }}
                    status={errors.exclude ? "error" : ""}
                  >
                    {excludeOptions.map((item) => (
                      <Select.Option key={item.key} value={item.key}>
                        {item.value}
                      </Select.Option>
                    ))}
                  </Select>
                  {/* {errors.exclude && (
                    <div className="text-danger small mt-1">
                      {errors.exclude}
                    </div>
                  )} */}
                </Form.Group>
              </Col>

              {/* Daily Intake */}
              <Col xs={12} md={3}>
                <FormLabel
                  label={trans("ClientMealPlan.mealIntake")}
                  required
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") +
                    " " +
                    trans("ClientMealPlan.mealIntake")
                  }
                  name="dailyIntake"
                  value={formData.dailyIntake}
                  onChange={handleChange}
                  isInvalid={!!errors.dailyIntake}
                  feedback={errors.dailyIntake}
                  className="rounded-3"
                  suffix="Cal"
                />
              </Col>
            </Row>

            {/* Cook time  */}
            <Row className="mt-4">
              <Col xs={6} md={3} lg={3}>
                <Form.Group controlId="cookTime" className="cook-time-group">
                  <FormLabel label="Cook Time" required />
                  <div className="cook-time-slider">
                    <input
                      type="range"
                      name="cookTime"
                      value={formData.cookTime}
                      onChange={handleChange}
                      min="0"
                      max="30"
                      className="custom-slider"
                      disabled={formData.noPreference}
                    />
                    <span className="cook-time-value">
                      {formData.cookTime} Min
                    </span>
                  </div>
                  <Form.Check
                    type="checkbox"
                    label="No Preference"
                    checked={formData.noPreference}
                    onChange={handleCheckboxChange}
                    className="mt-2"
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={6}>
                <Form.Group controlId="edgeCaseRequests">
                  <FormLabel label="Edge Case Requests" />

                  <FormControlTextArea
                    type="text"
                    name="edgeCaseRequests"
                    rows={3}
                    value={formData.edgeCaseRequests}
                    onChange={handleChange}
                    placeholder="Enter Edge Case Requests"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="align-items-center mt-2"></Row>
            <Row className="mt-4">
              <Col xs={12} md={6} lg={6}>
                <FormLabel label={trans("ClientMealPlan.meal")} required />
                <FormSelect
                  label={""}
                  name="meals"
                  value={selectedMeals}
                  onChange={handleMealsChange}
                  options={mealTimeAI}
                />
                {errors.selectedMeals && (
                  <div className="text-danger small mt-1">
                    {errors.selectedMeals}
                  </div>
                )}
              </Col>
              <Col xs={12} md={6} lg={6}>
                <FormLabel label="Macro Split Template" />
                <div className="macro-select-wrapper">
                  <Select
                    className="w-100"
                    placeholder="Select a macro split template"
                    value={selectedMacroTemplate}
                    loading={macroSplitTemplatesLoading}
                    onChange={handleMacroTemplateChange}
                  >
                    {macroSplitTemplates?.map((template) => (
                      <Option key={template.id} value={template.id}>
                        {template.planName}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
            </Row>
            <Row className="mt-2"></Row>
            <Row className="mt-2">
              {/* Protein */}
              <Col>
                <FormLabel
                  label={trans("ClientMealPlan.protein")}
                  className="text-green-500"
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") +
                    " " +
                    trans("ClientMealPlan.protein")
                  }
                  name="proteinPercentage"
                  value={formData.proteinPercentage}
                  onChange={handleChange}
                  isInvalid={!!errors.proteinPercentage}
                  className="custom-rounded-top"
                  suffix="%"
                  isDisabled={selectedMacroTemplate !== 11}
                />
                <FormControl
                  type="text"
                  placeholder={trans("Common.enter") + " g"}
                  name="proteinGrams"
                  value={formData.proteinGrams}
                  onChange={handleChange}
                  isDisabled
                  className="custom-rounded-bottom"
                  suffix="g"
                />
              </Col>

              {/* Carbs */}
              <Col>
                <FormLabel
                  label={trans("ClientMealPlan.carbs")}
                  className="text-yellow-500"
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") + " " + trans("ClientMealPlan.carbs")
                  }
                  name="carbsPercentage"
                  value={formData.carbsPercentage}
                  onChange={handleChange}
                  isInvalid={!!errors.carbsPercentage}
                  className="custom-rounded-top"
                  suffix="%"
                  isDisabled={
                    selectedMacroTemplate && selectedMacroTemplate !== 11
                  }
                />
                <FormControl
                  type="text"
                  placeholder={trans("Common.enter") + " g"}
                  name="carbsGrams"
                  value={formData.carbsGrams}
                  onChange={handleChange}
                  isDisabled
                  className="custom-rounded-bottom"
                  suffix="g"
                />
              </Col>

              {/* Fat */}
              <Col>
                <FormLabel
                  label={trans("ClientMealPlan.fat")}
                  className="text-gray-700"
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") + " " + trans("ClientMealPlan.fat")
                  }
                  name="fatPercentage"
                  value={formData.fatPercentage}
                  onChange={handleChange}
                  isInvalid={!!errors.fatPercentage}
                  className="custom-rounded-top"
                  suffix="%"
                  isDisabled={
                    selectedMacroTemplate && selectedMacroTemplate !== 11
                  }
                />
                <FormControl
                  type="text"
                  placeholder={trans("Common.enter") + " g"}
                  name="fatGrams"
                  value={formData.fatGrams}
                  onChange={handleChange}
                  isDisabled
                  className="custom-rounded-bottom"
                  suffix="g"
                />
              </Col>
            </Row>
            {!isPercentageValid && (
              <p className="m-4 ms-2 text-danger">
                {trans("ClientMealPlan.proteinPercentageError")}
              </p>
            )}

            <Col className="text-end mt-4">
              {/* Extracted button text logic */}
              {(() => {
                let buttonText;
                if (recipesLoading) {
                  buttonText = "Generating...";
                } else if (isSubmitted && hasResults) {
                  buttonText = "Re-Generate";
                } else {
                  buttonText = "Generate Recipe";
                }
                return (
                  <Button
                    type="submit"
                    className="create-button generate-recipe-btn"
                    disabled={recipesLoading}
                  >
                    {buttonText}
                  </Button>
                );
              })()}
            </Col>

            {isSubmitted && (
              <>
                <Row className="mt-2">
                  <h6>Recipes</h6>
                </Row>

                {recipesLoading && <Loader />}

                {!recipesLoading && hasResults && (
                  <div className="rcp-row mt-2">
                    {visibleRecipes.map((recipe) => {
                      const editedRecipe = editedRecipes.get(recipe.title);
                      const displayRecipe = editedRecipe || recipe;
                      const nutrients =
                        displayRecipe.nutritionPerServing ||
                        displayRecipe.nutrientsPerServing ||
                        {};

                      return (
                        <CardRecipes
                          key={displayRecipe.recipeId || displayRecipe.title}
                          title={displayRecipe.title}
                          description={
                            Array.isArray(displayRecipe.ingredients)
                              ? displayRecipe.ingredients
                                  .map((i) =>
                                    [
                                      i.quantityinMetric,
                                      i.measureinMetric,
                                      i.ingredient,
                                    ]
                                      .filter(Boolean)
                                      .join(" ")
                                  )
                                  .join(", ")
                              : ""
                          }
                          calories={nutrients.calories}
                          protein={nutrients.protein_g || nutrients.protein}
                          carbs={nutrients.carbs_g || nutrients.carbs}
                          fat={nutrients.fat_g || nutrients.fat}
                          imageBase64={
                            imagesByTitle[normalizeTitle(displayRecipe.title)]
                              ?.imageBase64
                          }
                          imageFileType={
                            imagesByTitle[normalizeTitle(displayRecipe.title)]
                              ?.imageFileType || "image/png"
                          }
                          selected={selectedTitles.has(displayRecipe.title)}
                          onSelect={(title) => {
                            if (showOnlySelected) return;
                            setSelectedTitles((prev) => {
                              const next = new Set(prev);
                              next.has(title)
                                ? next.delete(title)
                                : next.add(title);
                              return next;
                            });
                          }}
                          /** props for Info icon */
                          recipeId={displayRecipe.recipeId}
                          onInfo={handleInfoOpen}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {showRecipes && (
              <Col className="text-end mt-3">
                {(() => {
                  if (imagesLoading) {
                    return <Loader />;
                  }
                  if (canShowSave) {
                    return (
                      <Button
                        type="button"
                        className="create-button save-images-btn"
                        onClick={handleSaveReceipes}
                        disabled={addLoading}
                      >
                        {addLoading ? "Saving..." : "Save"}
                      </Button>
                    );
                  }
                  return (
                    <Button
                      type="button"
                      className="create-button generate-recipe-btn"
                      onClick={handleGenerateImages}
                      disabled={isGeneratingImages || selectedTitles.size === 0}
                    >
                      Generate Images
                    </Button>
                  );
                })()}
              </Col>
            )}
          </Form>
        </>
      )}

      <RecipeDrawerAI
        visible={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingRecipeId(null);
        }}
        recipe={drawerRecipe}
        onRecipeUpdate={handleRecipeUpdate}
        isCurrentlyEditing={editingRecipeId === drawerRecipe?.name}
      />

      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default Recipe;